import { useMemo, Fragment } from 'react';
import styled from 'styled-components';
import { componentsThemeMap, colorToVariable } from '../../componentsThemeMap.js';
import { ANALYTICS_KEY } from '../../constants.js';
import { AnalyticsProvider } from '../../providers/AnalyticsProvider.js';
import { useGlobalMicrocopy } from '../../providers/MicrocopyProvider.js';
import '../../ui/Div/index.js';
import { LinkPressable } from '../../ui/Link/LinkPressable.js';
import '../../ui/media/index.js';
import { Text } from '../../ui/Text.js';
import { jsx, jsxs, Fragment as Fragment$1 } from 'react/jsx-runtime';
import { Div } from '../../ui/Div/Div.js';
import { Media } from '../../ui/media/Lazymedia.js';
const DEFAULT_ALT_TEXT = 'Card Image';
function WayfindingCardArticle({
  headline,
  href,
  body,
  mediaUrl,
  mediaAlt,
  mediaType = 'image',
  thumbnailUrl,
  hasLightbox,
  readTime,
  datePublished: date,
  tagLabel,
  tagHref,
  style = 'default',
  largeThumbnail,
  long: renderAsLong,
  maxLines = 5,
  compact,
  aspectRatio = 'long'
}) {
  const _componentsThemeMap$s = componentsThemeMap[style],
    foreground = _componentsThemeMap$s.foreground,
    foregroundSecondary = _componentsThemeMap$s.foregroundSecondary,
    primary = _componentsThemeMap$s.primary;
  const microcopies = useGlobalMicrocopy();
  const LinkWrapper = useMemo(() => {
    if (!href) return Fragment;else {
      // eslint-disable-next-line no-inner-declarations -- TODO Fix this
      function Link({
        children
      }) {
        return /*#__PURE__*/jsx(Div, {
          display: "contents",
          css: {
            a: {
              display: 'contents'
            }
          },
          children: /*#__PURE__*/jsx(LinkPressable, {
            href: href,
            children: children
          })
        });
      }
      return Link;
    }
  }, [href]);
  const cardEyebrow = useMemo(() => /*#__PURE__*/jsxs(Div, {
    spacingTop: 2,
    gap: 0.5,
    alignItems: "center",
    css: {
      whiteSpace: 'nowrap'
    },
    children: [tagLabel && (tagHref ? /*#__PURE__*/jsx(LinkPressable, {
      href: tagHref,
      "data-qa": ANALYTICS_KEY.CardEyebrow,
      children: /*#__PURE__*/jsxs(Text, {
        variant: "legal",
        as: "p",
        color: primary,
        css: {
          textDecoration: 'underline'
        },
        children: [tagLabel, (date || readTime) && ',']
      })
    }) : /*#__PURE__*/jsxs(Text, {
      variant: "legal",
      as: "p",
      color: foreground,
      children: [tagLabel, (date || readTime) && ',']
    })), date && /*#__PURE__*/jsxs(Text, {
      variant: "legal",
      as: "p",
      color: foregroundSecondary,
      children: [date, readTime && ',']
    }), readTime && /*#__PURE__*/jsxs(Text, {
      variant: "legal",
      as: "p",
      color: foregroundSecondary,
      children: [readTime, ' ', mediaType === 'video' ? microcopies.global.videoDuration : microcopies.global.readTime]
    })]
  }), [date, readTime, tagHref, tagLabel, mediaType, foregroundSecondary, primary, foreground, microcopies]);
  const cardWidth = renderAsLong ? {
    sm: undefined,
    md: 200,
    lg: compact ? 278 : 379
  } : undefined;
  const imageHeight = aspectRatio === 'square' ? undefined : compact ? 146 : {
    sm: 172,
    md: 172,
    lg: largeThumbnail ? 311 : 202
  };
  const isVideo = mediaType === 'video';
  const isNoMedia = mediaType === 'none';
  const MediaLinkWrapper = isVideo ? Fragment : LinkWrapper;
  return /*#__PURE__*/jsxs(CardContainer, {
    flexDirection: renderAsLong ? {
      sm: 'column',
      md: 'row'
    } : 'column',
    alignItems: renderAsLong ? {
      sm: 'baseline',
      md: 'center'
    } : 'baseline',
    overflow: "hidden",
    $isVideo: isVideo,
    $hoverColor: primary,
    children: [/*#__PURE__*/jsx(AnalyticsProvider, {
      analyticsPrefix: ANALYTICS_KEY.CardImage,
      children: mediaType !== 'none' && /*#__PURE__*/
      // eslint-disable-next-line @cbhq/react-no-nested-component -- TODO Fix this
      jsx(MediaLinkWrapper, {
        children: /*#__PURE__*/jsx(Media, {
          alt: mediaAlt || DEFAULT_ALT_TEXT,
          src: mediaUrl,
          height: imageHeight,
          maxWidth: cardWidth,
          minWidth: cardWidth,
          className: IMAGE_CLASS,
          overflow: "hidden",
          mediaType: mediaType,
          thumbnailUrl: thumbnailUrl,
          thumbnailAlt: mediaAlt,
          hasLightbox: hasLightbox
        })
      })
    }), !renderAsLong && cardEyebrow, /*#__PURE__*/jsxs(Div, {
      flexDirection: "column",
      spacingStart: renderAsLong ? {
        sm: 0,
        md: isNoMedia ? 0 : 6
      } : 0,
      spacingTop: renderAsLong ? {
        sm: isNoMedia ? 0 : 2,
        md: 0
      } : {
        sm: 2
      },
      className: CONTENT_CLASS,
      flex: 1.7,
      children: [/*#__PURE__*/jsx(AnalyticsProvider, {
        analyticsPrefix: ANALYTICS_KEY.CardHeader,
        children: /*#__PURE__*/jsx(LinkWrapper, {
          children: /*#__PURE__*/jsxs(Fragment$1, {
            children: [/*#__PURE__*/jsx(Text, {
              variant: {
                sm: 'title4',
                lg: compact ? 'title4' : 'title2'
              },
              as: "h3",
              color: foreground,
              css: {
                display: '-webkit-box',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '-webkit-line-clamp': `${maxLines}`,
                '-webkit-box-orient': 'vertical'
              },
              children: headline
            }), body && /*#__PURE__*/jsx(Text, {
              variant: "body",
              spacingTop: 1,
              as: "p",
              color: foregroundSecondary,
              css: {
                display: '-webkit-box',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '-webkit-line-clamp': `${maxLines}`,
                '-webkit-box-orient': 'vertical'
              },
              children: body
            })]
          })
        })
      }), renderAsLong && cardEyebrow]
    })]
  });
}
const IMAGE_CLASS = 'card-article-image';
const CONTENT_CLASS = 'card-article-content';
const CardContainer = styled(Div).attrs(({
  $hoverColor = 'primary'
}) => ({
  style: {
    '--card-hover-color': colorToVariable($hoverColor)
  }
}))`
  .${IMAGE_CLASS} {
    border: solid 1px;
    border-color: ${colorToVariable('line')};
  }

  ${({
  $isVideo
}) => $isVideo ? `
        .${CONTENT_CLASS} {
          &:hover {
            h3 {
              color: var(--card-hover-color);
            }
          }
        }
      ` : `
        &:hover {
          h3 {
            color: var(--card-hover-color);
          }
        }
      `}
`;
export { WayfindingCardArticle };
//# sourceMappingURL=CardArticle.js.map
